/**
 * Security Rules Test Utility
 * Use this in your React app to test security rules with different user roles
 */

import { db } from '../firebase/config';
import { 
  doc, 
  getDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs
} from 'firebase/firestore';
import { auth } from '../firebase/config';

export interface TestResult {
  testName: string;
  passed: boolean;
  error?: string;
  message: string;
}

/**
 * Test admin access to user documents
 */
export async function testAdminReadAllUsers(targetUserId: string): Promise<TestResult> {
  try {
    const userDoc = await getDoc(doc(db, 'users', targetUserId));
    return {
      testName: 'Admin can read all users',
      passed: userDoc.exists(),
      message: userDoc.exists() 
        ? `✅ Successfully read user ${targetUserId}` 
        : `❌ User document does not exist`
    };
  } catch (error: any) {
    return {
      testName: 'Admin can read all users',
      passed: false,
      error: error.code,
      message: `❌ Permission denied: ${error.message}`
    };
  }
}

/**
 * Test admin can update user roles
 */
export async function testAdminUpdateRole(targetUserId: string, newRole: 'admin' | 'staff' | 'client'): Promise<TestResult> {
  try {
    await updateDoc(doc(db, 'users', targetUserId), {
      role: newRole,
      updatedAt: new Date()
    });
    return {
      testName: 'Admin can update user roles',
      passed: true,
      message: `✅ Successfully updated user ${targetUserId} role to ${newRole}`
    };
  } catch (error: any) {
    return {
      testName: 'Admin can update user roles',
      passed: false,
      error: error.code,
      message: `❌ Permission denied: ${error.message}`
    };
  }
}

/**
 * Test client can read own user document
 */
export async function testClientReadOwnUser(): Promise<TestResult> {
  const user = auth.currentUser;
  if (!user) {
    return {
      testName: 'Client can read own user',
      passed: false,
      message: '❌ User not authenticated'
    };
  }

  try {
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    return {
      testName: 'Client can read own user',
      passed: userDoc.exists(),
      message: userDoc.exists() 
        ? `✅ Successfully read own user document` 
        : `❌ User document does not exist`
    };
  } catch (error: any) {
    return {
      testName: 'Client can read own user',
      passed: false,
      error: error.code,
      message: `❌ Permission denied: ${error.message}`
    };
  }
}

/**
 * Test client cannot change their role
 */
export async function testClientCannotChangeRole(): Promise<TestResult> {
  const user = auth.currentUser;
  if (!user) {
    return {
      testName: 'Client cannot change role',
      passed: false,
      message: '❌ User not authenticated'
    };
  }

  try {
    await updateDoc(doc(db, 'users', user.uid), {
      role: 'admin' // This should fail
    });
    return {
      testName: 'Client cannot change role',
      passed: false,
      message: '❌ Security issue: Client was able to change role!'
    };
  } catch (error: any) {
    if (error.code === 'permission-denied') {
      return {
        testName: 'Client cannot change role',
        passed: true,
        message: '✅ Correctly denied: Client cannot change role'
      };
    }
    return {
      testName: 'Client cannot change role',
      passed: false,
      error: error.code,
      message: `❌ Unexpected error: ${error.message}`
    };
  }
}

/**
 * Test client can read own client document
 */
export async function testClientReadOwnClient(): Promise<TestResult> {
  const user = auth.currentUser;
  if (!user) {
    return {
      testName: 'Client can read own client document',
      passed: false,
      message: '❌ User not authenticated'
    };
  }

  try {
    // Try to find client document by email
    const clientsQuery = query(
      collection(db, 'clients'),
      where('email', '==', user.email)
    );
    const snapshot = await getDocs(clientsQuery);
    
    if (snapshot.empty) {
      // Try by userId
      const clientsQueryByUserId = query(
        collection(db, 'clients'),
        where('userId', '==', user.uid)
      );
      const snapshot2 = await getDocs(clientsQueryByUserId);
      
      return {
        testName: 'Client can read own client document',
        passed: !snapshot2.empty,
        message: snapshot2.empty 
          ? '⚠️ No client document found (may be expected if not created yet)'
          : '✅ Successfully read own client document'
      };
    }

    return {
      testName: 'Client can read own client document',
      passed: true,
      message: '✅ Successfully read own client document'
    };
  } catch (error: any) {
    return {
      testName: 'Client can read own client document',
      passed: false,
      error: error.code,
      message: `❌ Permission denied: ${error.message}`
    };
  }
}

/**
 * Test client cannot read other clients
 */
export async function testClientCannotReadOtherClients(otherClientId: string): Promise<TestResult> {
  try {
    await getDoc(doc(db, 'clients', otherClientId));
    return {
      testName: 'Client cannot read other clients',
      passed: false,
      message: '❌ Security issue: Client was able to read other client data!'
    };
  } catch (error: any) {
    if (error.code === 'permission-denied') {
      return {
        testName: 'Client cannot read other clients',
        passed: true,
        message: '✅ Correctly denied: Client cannot read other clients'
      };
    }
    return {
      testName: 'Client cannot read other clients',
      passed: false,
      error: error.code,
      message: `❌ Unexpected error: ${error.message}`
    };
  }
}

/**
 * Get current user role
 */
export async function getCurrentUserRole(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;

  try {
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (userDoc.exists()) {
      return userDoc.data().role || 'client';
    }
    return null;
  } catch (error) {
    console.error('Error getting user role:', error);
    return null;
  }
}

/**
 * Run all tests for current user
 */
export async function runAllSecurityTests(): Promise<TestResult[]> {
  const results: TestResult[] = [];
  const user = auth.currentUser;
  
  if (!user) {
    results.push({
      testName: 'Authentication',
      passed: false,
      message: '❌ User not authenticated. Please sign in first.'
    });
    return results;
  }

  const role = await getCurrentUserRole();
  results.push({
    testName: 'User Role',
    passed: role !== null,
    message: role ? `✅ Current role: ${role}` : '❌ Could not determine role'
  });

  // Admin tests
  if (role === 'admin') {
    results.push(await testAdminReadAllUsers(user.uid));
    // Note: We skip role update test to avoid changing actual data
  }

  // Client tests
  if (role === 'client') {
    results.push(await testClientReadOwnUser());
    results.push(await testClientCannotChangeRole());
    results.push(await testClientReadOwnClient());
  }

  return results;
}



